/**
 * Connections (`/services`, `/services/repositories`, `/services/system`,
 * `/services/mcp`, `/services/webhooks`): the tools the Teams read and write,
 * on the queue grammar. One header with the tabs as chips (each an address):
 * Services, Repositories, System (only where the workspace has running
 * copies), MCP servers and Outbound webhooks, each a kind of connection
 * rather than a name shared with the page. The open
 * tab's rows grouped by what the reader owes them, and the record in the
 * sheet. The tab components own their reads and their sheets; the page owns
 * the header, the Refresh beat every tab re-reads on, and the Custom
 * connection sheet that hands the wiring to @yagni in the Ask dock.
 */
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useCan } from "../hooks/useWorkspaceRole.js";
import { Btn, ListHead } from "../queue/index.js";
import { cx } from "../queue/primitives.js";
import { DockScope } from "../shell/DockScope.js";
import { connectionsScope } from "../shell/yagniRailScopes.js";
import { ConnectionsTab } from "./ConnectionsTab.js";
import { CustomConnectionSheet } from "./CustomConnectionSheet.js";
import { McpTab } from "./McpTab.js";
import { RepositoriesTab } from "./RepositoriesTab.js";
import { SystemTabBody, useWorkspaceSystem } from "./SystemTab.js";
import { systemApi } from "./system/systemApi.js";
import { WebhooksTab } from "./WebhooksTab.js";
import type { AskSeed } from "../shell/askDockStore.js";
import { useOwnedSheet } from "../shell/useOwnedSheet.js";

export type ConnectionsTabKey = "connections" | "repositories" | "system" | "mcp" | "webhooks";

export const CONNECTIONS_TABS: Array<{
  key: ConnectionsTabKey;
  label: string;
  path: string;
  /** Shown only where the workspace has running copies (the System read answers). */
  gated?: true;
}> = [
  { key: "connections", label: "Services", path: "/services" },
  { key: "repositories", label: "Repositories", path: "/services/repositories" },
  { key: "system", label: "System", path: "/services/system", gated: true },
  { key: "mcp", label: "MCP servers", path: "/services/mcp" },
  { key: "webhooks", label: "Outbound webhooks", path: "/services/webhooks" },
];

/** The tab an address opens; anything unknown under /services is Connections. */
export function tabForPath(pathname: string): ConnectionsTabKey {
  const segment = pathname.replace(/^\/services\/?/, "").split("/")[0] ?? "";
  return (
    CONNECTIONS_TABS.find((tab) => tab.key === segment && tab.key !== "connections")?.key ??
    "connections"
  );
}

/** What every tab is handed by the page. */
export interface ConnectionsTabProps {
  /** Bumped by the header's Refresh: re-read everything the tab shows. */
  refreshKey: number;
}

/** What a member without manage-repos reads on the Repositories tab. */
export const REPOSITORIES_STUB = "Ask a workspace admin to set up repository environments.";

export function ConnectionsPage() {
  const { pathname } = useLocation();
  const tab = tabForPath(pathname);
  const [refreshKey, setRefreshKey] = useState(0);
  const [customOpen, setCustomOpen] = useState(false);
  const canManageConnections = useCan("manage-connections");
  const canManageRepos = useCan("manage-repos", true);

  const [seed, setSeed] = useState<AskSeed | null>(null);
  const system = useWorkspaceSystem(systemApi, refreshKey);

  useOwnedSheet(
    customOpen ? (
      <CustomConnectionSheet
        onCancel={() => setCustomOpen(false)}
        onStart={(text) => {
          setCustomOpen(false);
          setSeed({ id: Date.now(), text });
        }}
      />
    ) : null,
    () => setCustomOpen(false),
  );

  const chips = CONNECTIONS_TABS.filter(
    (entry) => !entry.gated || system.status === "ready" || entry.key === tab,
  ).map((entry) => (
    <Link
      key={entry.key}
      to={entry.path}
      className={cx("chip tab", entry.key === tab && "on")}
      aria-current={entry.key === tab ? "page" : undefined}
    >
      {entry.label}
    </Link>
  ));

  return (
    <div className="wsurf" data-testid="connections-surface">
      <ListHead
        title="Connections"
        sub={[
          "The tools the Teams read and write, and who can see what",
          "Writes YAGNI makes for you are attributed to your own account",
        ]}
        acts={
          <>
            {canManageConnections && (
              <Btn variant="secondary" size="sm" onClick={() => setCustomOpen(true)}>
                Custom connection
              </Btn>
            )}
            <Btn variant="quiet" size="sm" onClick={() => setRefreshKey((n) => n + 1)}>
              Refresh
            </Btn>
          </>
        }
        chips={
          <nav className="tabs" aria-label="Connections sections">
            {chips}
          </nav>
        }
      />
      {tab === "connections" ? (
        <ConnectionsTab refreshKey={refreshKey} />
      ) : tab === "repositories" ? (
        canManageRepos ? (
          <RepositoriesTab refreshKey={refreshKey} />
        ) : (
          <div className="qlist">
            <div className="empty dashed tight" data-testid="integrations-repositories-stub">
              <p>{REPOSITORIES_STUB}</p>
            </div>
          </div>
        )
      ) : tab === "system" ? (
        <SystemTabBody status={system.status} data={system.data} />
      ) : tab === "mcp" ? (
        <McpTab refreshKey={refreshKey} />
      ) : (
        <WebhooksTab refreshKey={refreshKey} />
      )}
      <DockScope scope={connectionsScope()} seedRequest={seed} onSeedSent={() => setSeed(null)} />
    </div>
  );
}
